/**
 * Преобразование записи параболы из одного вида в другой.
 *
 * Кривая при этом не меняется — меняется только то, как она записана, и
 * ползунки под записью. В этом весь смысл: `y = x² − 6x + 13` и
 * `y = (x − 3)² + 4` — одна и та же парабола, и пока ученик не проделает
 * переход руками, он этого не знает.
 *
 * Поэтому здесь не только результат, но и выкладка: шаги считаются из тех
 * самых a, b, c, которые стоят в карточке сейчас. Пример с чужими числами
 * ученик списывает, пример со своими — проверяет.
 *
 * Арифметика точная. Выделение полного квадрата даёт h = −b/(2a) и
 * k = c − b²/(4a) — числа с делением, и в 0,1 после запятой выкладка перестаёт
 * сходиться с графиком.
 */

import {
  exact, isZero, mulExact, negExact, type ExactNumber,
} from "./numbers";
import {
  MINUS, familyShape, formulaText, generalForm, halfCoefficient, numberText, shiftedX,
  signedTerm, standardText, tail, toStandardForm, toVertexForm, withVariable,
} from "./math";
import type { CurveFamily, FunctionCurve, ParabolaCurve, VertexCurve } from "./model";

/** Шаг выкладки: зачем он и что получилось. */
export type ConversionStep = { why: string; text: string };

export type Conversion = {
  /** Название приёма — заголовок выкладки. */
  method: string;
  /** Целевой вид буквами: `y = a(x − h)² + k`. О нём спрашивает окно. */
  targetShape: string;
  target: CurveFamily;
  /** Та же кривая в новой записи. */
  result: FunctionCurve;
  steps: ConversionStep[];
};

function isOne(value: ExactNumber): boolean {
  return value.numerator === 1 && value.denominator === 1;
}

/**
 * Обёртка `a(...)`. При a = 1 скобки не ставятся: группировать в них нечего,
 * а лишняя пара скобок в каждой строке выкладки читается как часть приёма.
 */
function scaled(a: ExactNumber, inner: string): string {
  return isOne(a) ? inner : withVariable(a, `(${inner})`);
}

/** Есть ли у записи второй вид и какой. `null` — вид единственный. */
export function conversionTarget(curve: FunctionCurve): CurveFamily | null {
  if (curve.kind === "parabola") return "vertex";
  if (curve.kind === "vertex") return "parabola";
  return null;
}

/** Почему преобразовать нельзя, или `null` — можно. */
export function conversionBlocked(curve: FunctionCurve): string | null {
  if (curve.kind !== "parabola") return null;
  return isZero(curve.a)
    ? "При a = 0 это прямая, а не парабола: выделять квадрат не из чего"
    : null;
}

/** Выделение полного квадрата: `ax² + bx + c` → `a(x − h)² + k`. */
function squareOut(curve: ParabolaCurve): Conversion | null {
  const vertex = toVertexForm(curve);
  const p = halfCoefficient(curve);
  if (!vertex || !p) return null;

  const method = "Выделение полного квадрата";
  const head = {
    method,
    targetShape: familyShape("vertex"),
    target: "vertex" as CurveFamily,
    result: vertex,
  };
  const start = { why: "Что есть", text: standardText(curve.a, curve.b, curve.c) };
  const done = { why: "Вершина стоит в скобке", text: formulaText(vertex) };

  // Слагаемого с x нет — квадрат уже выделен, и выкладка была бы про нули.
  if (isZero(curve.b)) {
    return {
      ...head,
      steps: [start, { why: "Слагаемого с x нет — вершина уже на оси y", text: formulaText(vertex) }],
    };
  }

  // Коэффициент при x внутри скобки (b/a) и квадрат его половины.
  const inner = mulExact(exact(2), p);
  const square = mulExact(p, p);
  const steps: ConversionStep[] = [start];

  if (!isOne(curve.a)) {
    steps.push({
      why: `Выносим a = ${numberText(curve.a)} за скобку`,
      text: `y = ${scaled(curve.a, `x²${signedTerm(inner, "x")}`)}${tail(curve.c)}`,
    });
  }
  steps.push({
    why: "Берём половину коэффициента при x",
    text: `${numberText(inner)} : 2 = ${numberText(p)}`,
  });
  steps.push({
    why: `Добавляем и вычитаем её квадрат: (${numberText(p)})² = ${numberText(square)}`,
    text: `y = ${scaled(
      curve.a,
      `x²${signedTerm(inner, "x")} + ${numberText(square)} ${MINUS} ${numberText(square)}`,
    )}${tail(curve.c)}`,
  });
  steps.push({
    why: "Первые три слагаемых свёртываются в квадрат",
    text: `y = ${scaled(
      curve.a,
      `(${shiftedX(vertex.h)})² ${MINUS} ${numberText(square)}`,
    )}${tail(curve.c)}`,
  });
  if (!isOne(curve.a)) {
    steps.push({
      why: "Раскрываем внешнюю скобку",
      text: `y = ${withVariable(curve.a, `(${shiftedX(vertex.h)})²`)}`
        + `${tail(negExact(mulExact(curve.a, square)))}${tail(curve.c)}`,
    });
  }
  steps.push(done);
  return { ...head, steps };
}

/** Раскрытие скобок: `a(x − h)² + k` → `ax² + bx + c`. */
function expand(curve: VertexCurve): Conversion {
  const result = toStandardForm(curve);
  const { A, B, C } = generalForm(curve);
  const head = {
    method: "Раскрытие скобок",
    targetShape: familyShape("parabola"),
    target: "parabola" as CurveFamily,
    result,
  };
  const start = { why: "Что есть", text: formulaText(curve) };

  // Сдвига нет — в скобке стоит просто x, и раскрывать нечего.
  if (isZero(curve.h)) {
    return {
      ...head,
      steps: [start, { why: "Сдвига по горизонтали нет — в скобке уже x", text: formulaText(result) }],
    };
  }

  const double = negExact(mulExact(exact(2), curve.h));
  const square = mulExact(curve.h, curve.h);
  const squareText = `x²${signedTerm(double, "x")}${tail(square)}`;
  const steps: ConversionStep[] = [
    start,
    { why: "Раскрываем квадрат разности", text: `(${shiftedX(curve.h)})² = ${squareText}` },
  ];
  if (!isOne(curve.a)) {
    steps.push({
      why: `Умножаем на a = ${numberText(curve.a)}`,
      text: `${withVariable(curve.a, `(${squareText})`)} = `
        + `${withVariable(A, "x²")}${signedTerm(B, "x")}${tail(mulExact(curve.a, square))}`,
    });
  }
  steps.push({
    why: `Прибавляем k = ${numberText(curve.k)} к свободному члену`,
    text: standardText(A, B, C),
  });
  return { ...head, steps };
}

/** Преобразование записи или `null`, если его нет: у прямой и гиперболы. */
export function conversionOf(curve: FunctionCurve): Conversion | null {
  if (curve.kind === "parabola") return squareOut(curve);
  if (curve.kind === "vertex") return expand(curve);
  return null;
}
